import { GET_COMPANY_PROFILE, GET_SHOP_LOCATIONS } from 'api/wenjetso/graphql/queries'
import { CREATE_COMPANY_PROFILE, UPDATE_COMPANY_PROFILE, CREATE_SHOP_LOCATION, DELETE_SHOP_LOCATION } from 'api/wenjetso/graphql/mutations'
// eslint-disable-next-line import/no-cycle
import { authStoreId } from 'stores'

/**
 * Company Store
 * 
 * @typedef {object} CompanyStore
 * @property {CompanyState} state - Company state
 * @property {CompanyAction} actions - Company actions
 * */


/**
 * Company State
 * 
 * @typedef {object} CompanyState
 * @property {object} profile - Company profile
 */
const initState = {
  companyProfile: null,
  isLoaded: false,
  shopLocations: []
}

const actionTypes = {
  setCompanyProfile: 'setCompanyProfile',
  setShopLocations: 'setShopLocations'
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.setCompanyProfile: {
      return {
        ...state,
        companyProfile: action.profile,
        isLoaded: true,
      }
    }
    case actionTypes.setShopLocations: {
      return {
        ...state,
        shopLocations: action.locations,
      }
    }
    default:
      return state
  }
}

/**
 * Company Getters
 * 
 * @typedef {object} CompanyGetter
 * @property {Function} getCompanyProfile - Get Company profile
 */

/** @type {import('libs/store').StoreGettersInterface} */
// eslint-disable-next-line func-names
const getters = function ({state}) {
  this.getCompanyProfile = () => state.companyProfile
  this.isProfileReady = () => state.isLoaded
  this.shopLocationList = () => state.shopLocations
  return this
}

/**
 * Company Actions
 * 
 * @typedef {object} Companyction
 * @property {Function} fetchCompanyProfile - Fetch Company profile
 */

 /** @type {import('libs/store').StoreActionsInterface} */
// eslint-disable-next-line func-names
const actions = function ({ dispatch, plugins, state, useStore }) {
  const { requestGraphQL, requestGraphQLFiles } = plugins
  const setCompanyProfile = (profile) => {
    dispatch({type : actionTypes.setCompanyProfile, profile})
  }
  const setShopLocationList = (locations) => {
    dispatch({type : actionTypes.setShopLocations, locations})
  }

  this.clear = () => {
    setCompanyProfile(null)
    setShopLocationList([])
  }

  this.fetchCompany = async () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(GET_COMPANY_PROFILE, {}, authStore.getters.getAccessToken())
    if (!response.error) {
      const payload = response.comp_list_company_profile
      if (Array.isArray(payload) && payload.length > 0) {
        setCompanyProfile(payload[0])
      } else {
        setCompanyProfile(null)
      }
    }
    return response
  }
  this.createCompanyProfile = async ({
    name,
    url,
    email,
    telephone,
    file,
  }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQLFiles(CREATE_COMPANY_PROFILE, {
      input: {
        name,
        url,
        email,
        telephone,
      }
    }, file ? {
      fieldName: 'profileFile',
      file
    } : null, authStore.getters.getAccessToken(), {
      format: 'formData'
    })
    if (!response.error) {
      const payload = response.create_company_profile
      setCompanyProfile(payload)
    }
    return response
  }
  this.updateCompanyProfile = async ({
    name,
    url,
    email,
    telephone,
    description
  }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(UPDATE_COMPANY_PROFILE, {
      input: {
        company_id: state.companyProfile._id,
        name,
        url,
        email,
        telephone,
        description
      }
    }, authStore.getters.getAccessToken())
    if (!response.error) {
      const payload = response.update_company_profile
      setCompanyProfile(payload)
    }
    return response
  }
  this.uploadCompanyProfileImage = async (file) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQLFiles(UPDATE_COMPANY_PROFILE, {
      input: {
        company_id: state.companyProfile._id,
      }
    }, {
      fieldName: 'profileFile',
      file
    }, authStore.getters.getAccessToken(), {
      format: 'formData'
    })
    if (!response.error) {
      const payload = response.update_company_profile
      setCompanyProfile(payload)
    }
    return response
  }
  this.createShopLocation = async ({name, shortCode, telephone, gelocation, address, categoryCode}) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(CREATE_SHOP_LOCATION, {
      input: {
        company_id: state.companyProfile._id,
        address,
        regional_code: shortCode,
        category_code: categoryCode,
        name,
        telephone,
        lat: gelocation.lat,
        lng: gelocation.lng,
      }
    }, authStore.getters.getAccessToken())
    if (!response.error) {
      const payload = response.create_location
      state.shopLocations.push(payload)
      setShopLocationList(state.shopLocations)
    }
    return response
  }
  this.deleteShopLocation = async ({locationId}) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(DELETE_SHOP_LOCATION, {
      input: {
        company_id: state.companyProfile._id,
        location_id: locationId,
      }
    }, authStore.getters.getAccessToken())
    if (!response.error) {
      const locations = state.shopLocations.filter(location => location._id !== locationId)
      setShopLocationList(locations)
    }
    return response
  }
  this.fetchShopLocations = async () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(GET_SHOP_LOCATIONS, {
      company_id: state.companyProfile._id,
    }, authStore.getters.getAccessToken())
    if (!response.error) {
      const payload = response.list_company_location
      setShopLocationList(payload)
    }
    return response
  }

  return this
}

 /**
  * @type {import('libs/store').StoreInterface}
  */
export const companyStore = {
  initState,
  reducer,
  getters,
  actions,
}